import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, List, ListItem, ListItemText, ListItemIcon, TextField, Snackbar, Alert, CircularProgress, Typography, Box, Divider } from '@mui/material';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, doc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

interface JoinGroupDialogProps {
  open: boolean;
  onClose: () => void;
}

const JoinGroupDialog: React.FC<JoinGroupDialogProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const [myGroups, setMyGroups] = useState<any[]>([]);
  const [selectedToLeave, setSelectedToLeave] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedToJoin, setSelectedToJoin] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // 取得已加入群組
  useEffect(() => {
    if (!open || !user?.email) return;
    setLoading(true);
    setSelectedToLeave([]);
    setSelectedToJoin([]);
    setSearch('');
    setSearchResults([]);
    const fetchGroups = async () => {
      if (!user?.email) return;
      const userEmailLower = user.email.toLowerCase(); // 🔧 修復：統一轉小寫
      const q = query(collection(db, 'groups'), where('membersEmail', 'array-contains', userEmailLower)); // 🔧 修復：使用小寫
      const snap = await getDocs(q);
      setMyGroups(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchGroups();
  }, [open, user]);

  // 勾選退出
  const handleLeave = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const userEmailLower = user.email.toLowerCase(); // 🔧 修復：統一轉小寫
      for (const groupId of selectedToLeave) {
        const groupRef = doc(db, 'groups', groupId);
        await updateDoc(groupRef, {
          members: arrayRemove({ email: userEmailLower }), // 🔧 修復：使用小寫
          membersEmail: arrayRemove(userEmailLower) // 🔧 修復：使用小寫
        });
      }
      setSnackbar({ open: true, message: '已退出選取的群組', severity: 'success' });
      setSelectedToLeave([]);
      // 重新載入
      const q = query(collection(db, 'groups'), where('membersEmail', 'array-contains', userEmailLower)); // 🔧 修復：使用小寫
      const snap = await getDocs(q);
      setMyGroups(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      setSnackbar({ open: true, message: '退出群組失敗', severity: 'error' });
    }
    setLoading(false);
  };

  // 搜尋群組
  const handleSearch = async () => {
    if (!search.trim() || !user?.email) return;
    setSearching(true);
    try {
      const q = query(collection(db, 'groups'), where('name', '>=', search.trim()), where('name', '<=', search.trim() + '\uf8ff'));
      const snap = await getDocs(q);
      // 過濾掉已加入的
      const found = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(g => !myGroups.some(mg => mg.id === g.id));
      setSearchResults(found);
    } catch (e) {
      setSnackbar({ open: true, message: '搜尋失敗', severity: 'error' });
    }
    setSearching(false);
  };

  // 勾選加入
  const handleJoin = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const userEmailLower = user.email.toLowerCase(); // 🔧 修復：統一轉小寫
      for (const groupId of selectedToJoin) {
        const groupRef = doc(db, 'groups', groupId);
        await updateDoc(groupRef, {
          members: arrayUnion({ email: userEmailLower, isAdmin: false, name: '', profile: '', joinedAt: new Date() }), // 🔧 修復：使用小寫
          membersEmail: arrayUnion(userEmailLower) // 🔧 修復：使用小寫
        });
        // TODO: 呼叫後端 API 寄 email 通知管理員
      }
      setSnackbar({ open: true, message: '成功加入選取的群組', severity: 'success' });
      setSelectedToJoin([]);
      setSearchResults([]);
      setSearch('');
      // 重新載入（複用上面已宣告的 userEmailLower）
      const reloadQuery = query(collection(db, 'groups'), where('membersEmail', 'array-contains', userEmailLower)); // 🔧 修復：使用小寫
      const reloadSnap = await getDocs(reloadQuery);
      setMyGroups(reloadSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      setSnackbar({ open: true, message: '加入群組失敗', severity: 'error' });
    }
    setLoading(false);
  };

  if (!user?.email) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 24, color: '#1976d2', letterSpacing: 1 }}>
        {user?.email + ' 想加入哪個群組？'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
          你已加入的群組：{myGroups.length} 個
        </Typography>
        {loading ? <CircularProgress /> : (
          <List>
            {myGroups.map(g => (
              <ListItem key={g.id} dense sx={{ py: 0.5, minHeight: 32 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox checked={selectedToLeave.includes(g.id)} onChange={e => {
                    setSelectedToLeave(val => e.target.checked ? [...val, g.id] : val.filter(id => id !== g.id));
                  }} />
                </ListItemIcon>
                <ListItemText primary={g.name} primaryTypographyProps={{ fontSize: 16 }} />
              </ListItem>
            ))}
          </List>
        )}
        <Button variant="outlined" color="error" disabled={!selectedToLeave.length || loading} onClick={handleLeave} sx={{ mb: 2 }}>退出選取群組</Button>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontWeight: 700, mb: 1 }}>搜尋並加入新群組：</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField value={search} onChange={e => setSearch(e.target.value)} label="輸入群組名稱" fullWidth size="small" />
          <Button variant="contained" onClick={handleSearch} disabled={searching}>搜尋</Button>
        </Box>
        {searching ? <CircularProgress /> : (
          <List>
            {searchResults.map(g => (
              <ListItem key={g.id} dense>
                <ListItemIcon>
                  <Checkbox checked={selectedToJoin.includes(g.id)} onChange={e => {
                    setSelectedToJoin(val => e.target.checked ? [...val, g.id] : val.filter(id => id !== g.id));
                  }} />
                </ListItemIcon>
                <ListItemText primary={g.name} />
              </ListItem>
            ))}
          </List>
        )}
        <Button variant="contained" color="primary" disabled={!selectedToJoin.length || loading} onClick={handleJoin}>加入選取群組</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">關閉</Button>
      </DialogActions>
      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default JoinGroupDialog; 